import { useEffect, useRef, useState, useCallback } from "react";
import { X, RotateCcw, RotateCw, FlipHorizontal2, FlipVertical2, Undo2, Redo2, RefreshCcw, Lock, LockOpen, ZoomIn, ZoomOut, Download } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const RATIOS = [
  { label: "1:1", w: 1000, h: 1000 },
  { label: "4:5", w: 1000, h: 1250 },
  { label: "3:4", w: 1200, h: 1600 },
  { label: "16:9", w: 1600, h: 900 },
  { label: "Original", w: null, h: null },
];
const FITS = [["contain", "Fit (Contain)"], ["cover", "Fill (Cover)"], ["fill", "Stretch"], ["original", "Original Size"]];
const fullUrl = (url) => url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

export default function ImageEditor({ item, onClose, onSaved }) {
  const [img, setImg] = useState(null);
  const [locked, setLocked] = useState(true);
  const [state, setState] = useState({
    w: 1000, h: 1000, fit: "contain", zoom: 1, offset: { x: 0, y: 0 },
    rot: 0, flipH: false, flipV: false, bgType: "white", bgColor: "#ffffff",
  });
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [saving, setSaving] = useState("");
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const full = fullUrl(item.url);
  useEffect(() => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => setImg(im);
    im.onerror = () => toast.error("Could not load image for editing");
    im.src = full;
  }, [full]);

  const commit = (partial) => {
    setHistory((h) => [...h.slice(-29), state]);
    setFuture([]);
    setState((s) => ({ ...s, ...partial }));
  };
  const undo = () => {
    if (!history.length) return;
    setFuture((f) => [state, ...f]);
    setState(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };
  const redo = () => {
    if (!future.length) return;
    setHistory((h) => [...h, state]);
    setState(future[0]);
    setFuture((f) => f.slice(1));
  };
  const reset = () => commit({ w: 1000, h: 1000, fit: "contain", zoom: 1, offset: { x: 0, y: 0 }, rot: 0, flipH: false, flipV: false, bgType: "white", bgColor: "#ffffff" });

  const draw = useCallback((ctx, W, H, guides = false) => {
    if (!img) return;
    ctx.clearRect(0, 0, W, H);
    if (state.bgType !== "transparent") {
      ctx.fillStyle = state.bgType === "custom" ? state.bgColor : "#ffffff";
      ctx.fillRect(0, 0, W, H);
    }
    const rotated = state.rot % 180 !== 0;
    const iw = rotated ? img.height : img.width;
    const ih = rotated ? img.width : img.height;
    let sx, sy;
    if (state.fit === "fill") { sx = (W / iw) * state.zoom; sy = (H / ih) * state.zoom; }
    else {
      const base = state.fit === "cover" ? Math.max(W / iw, H / ih)
        : state.fit === "original" ? 1
        : Math.min(W / iw, H / ih);
      sx = sy = base * state.zoom;
    }
    ctx.save();
    ctx.translate(W / 2 + state.offset.x, H / 2 + state.offset.y);
    ctx.rotate((state.rot * Math.PI) / 180);
    ctx.scale(sx * (state.flipH ? -1 : 1), sy * (state.flipV ? -1 : 1));
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    ctx.restore();
    if (guides) {
      ctx.strokeStyle = "rgba(212,175,55,0.55)";
      ctx.lineWidth = Math.max(1, W / 500);
      ctx.setLineDash([W / 60, W / 80]);
      [[W / 2, 0, W / 2, H], [0, H / 2, W, H / 2], [W / 3, 0, W / 3, H], [(2 * W) / 3, 0, (2 * W) / 3, H], [0, H / 3, W, H / 3], [0, (2 * H) / 3, W, (2 * H) / 3]].forEach(([a, b, c, d]) => {
        ctx.beginPath(); ctx.moveTo(a, b); ctx.lineTo(c, d); ctx.stroke();
      });
      ctx.setLineDash([]);
    }
  }, [img, state]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !img) return;
    c.width = state.w;
    c.height = state.h;
    draw(c.getContext("2d"), state.w, state.h, true);
  }, [draw, img, state.w, state.h]);

  const onPointerDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: state.offset.x, oy: state.offset.y, scale: state.w / rect.width };
    e.target.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setState((s) => ({ ...s, offset: { x: d.ox + (e.clientX - d.sx) * d.scale, y: d.oy + (e.clientY - d.sy) * d.scale } }));
  };
  const onPointerUp = () => {
    if (dragRef.current) { dragRef.current = null; setHistory((h) => [...h.slice(-29), { ...state, offset: { ...state.offset } }]); setFuture([]); }
  };

  const setSize = (key, val) => {
    const n = Math.max(50, Math.min(4000, parseInt(val, 10) || 50));
    if (locked) {
      const ratio = state.w / state.h;
      commit(key === "w" ? { w: n, h: Math.round(n / ratio) } : { h: n, w: Math.round(n * ratio) });
    } else commit({ [key]: n });
  };

  const exportBlob = () => new Promise((resolve) => {
    const c = document.createElement("canvas");
    c.width = state.w;
    c.height = state.h;
    draw(c.getContext("2d"), state.w, state.h, false);
    const png = state.bgType === "transparent";
    c.toBlob(resolve, png ? "image/png" : "image/jpeg", 0.92);
  });

  const save = async (mode) => {
    if (saving) return;
    setSaving(mode);
    try {
      const blob = await exportBlob();
      const png = state.bgType === "transparent";
      const fname = `${(item.filename || "image").replace(/\.[a-z]+$/i, "")}-edited.${png ? "png" : "jpg"}`;
      const fd = new FormData();
      fd.append("file", new File([blob], fname, { type: png ? "image/png" : "image/jpeg" }));
      if (mode === "replace") {
        await api.post(`/admin/media/${item.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Image replaced — the original is kept and can be restored anytime");
      } else {
        fd.append("alt", item.alt || item.filename);
        const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (item.product_id) await api.post("/admin/media/assign", { ids: [data.id], product_id: item.product_id, add_to_product: false }).catch(() => {});
        toast.success("Saved as a new image (original untouched)");
      }
      onSaved();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setSaving(""); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 overflow-y-auto" data-testid="image-editor">
      <div className="bg-[#FAF8F5] min-h-screen lg:min-h-0 lg:my-6 max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="font-serif text-2xl">Image Editor</h2>
            <p className="text-xs text-[#8C7A5E]">{item.filename} · output {state.w}×{state.h}px · {state.bgType === "transparent" ? "PNG (transparent)" : "JPEG optimized"}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={undo} disabled={!history.length} data-testid="ie-undo" className="tq-btn-outline !py-2 !px-3 disabled:opacity-40"><Undo2 size={14} /> Undo</button>
            <button onClick={redo} disabled={!future.length} data-testid="ie-redo" className="tq-btn-outline !py-2 !px-3 disabled:opacity-40"><Redo2 size={14} /> Redo</button>
            <button onClick={reset} data-testid="ie-reset" className="tq-btn-outline !py-2 !px-3"><RefreshCcw size={14} /> Reset</button>
            <button onClick={onClose} aria-label="Close editor" data-testid="ie-close" className="tq-btn-outline !py-2 !px-3"><X size={16} /></button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-6">
          <div className="bg-[#121212] flex items-center justify-center p-4 relative min-h-[320px]">
            {img ? (
              <canvas ref={canvasRef} data-testid="ie-canvas"
                className="max-w-full max-h-[62vh] w-auto h-auto cursor-grab active:cursor-grabbing touch-none shadow-2xl"
                onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp} />
            ) : <p className="text-white/60 text-sm">Loading image…</p>}
          </div>

          <div className="space-y-5">
            <div>
              <p className="tq-overline mb-2">Canvas Ratio / Crop</p>
              <div className="flex flex-wrap gap-1.5">
                {RATIOS.map((r) => (
                  <button key={r.label} onClick={() => commit(r.w ? { w: r.w, h: r.h } : img ? { w: img.width, h: img.height } : {})}
                    data-testid={`ie-ratio-${r.label.replace(":", "x").toLowerCase()}`}
                    className={`px-2.5 py-1.5 text-[11px] border ${state.w === r.w && state.h === r.h ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`}>
                    {r.label}{r.w ? <span className="text-[9px] text-[#9a938a] ml-1">{r.w}×{r.h}</span> : ""}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="tq-overline">Resize (px)</p>
                <button onClick={() => setLocked(!locked)} data-testid="ie-lock" className="flex items-center gap-1 text-[11px] text-[#8C7A5E]">
                  {locked ? <Lock size={12} /> : <LockOpen size={12} />} {locked ? "Ratio locked" : "Unlocked"}
                </button>
              </div>
              <div className="flex gap-2 items-center">
                <input type="number" value={state.w} onChange={(e) => setSize("w", e.target.value)} data-testid="ie-width" className="tq-input !py-2 w-1/2" />
                <span className="text-[#9a938a]">×</span>
                <input type="number" value={state.h} onChange={(e) => setSize("h", e.target.value)} data-testid="ie-height" className="tq-input !py-2 w-1/2" />
              </div>
            </div>
            <div>
              <p className="tq-overline mb-2">Fit</p>
              <div className="grid grid-cols-2 gap-1.5">
                {FITS.map(([v, l]) => (
                  <button key={v} onClick={() => commit({ fit: v })} data-testid={`ie-fit-${v}`}
                    className={`px-2 py-1.5 text-[11px] border ${state.fit === v ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`}>{l}</button>
                ))}
              </div>
              <p className="text-[10px] text-[#9a938a] mt-1.5">Fit = whole product visible · Cover = fills canvas (may crop edges)</p>
            </div>
            <div>
              <p className="tq-overline mb-2">Zoom & Position</p>
              <div className="flex items-center gap-2">
                <button onClick={() => commit({ zoom: Math.max(0.1, +(state.zoom - 0.1).toFixed(2)) })} data-testid="ie-zoom-out" className="p-2 border border-[#E5DEC9] bg-white"><ZoomOut size={14} /></button>
                <input type="range" min="0.1" max="4" step="0.01" value={state.zoom}
                  onChange={(e) => setState((s) => ({ ...s, zoom: Number(e.target.value) }))}
                  onPointerUp={() => { setHistory((h) => [...h.slice(-29), { ...state }]); setFuture([]); }}
                  data-testid="ie-zoom" className="flex-1 accent-[#D4AF37]" />
                <button onClick={() => commit({ zoom: Math.min(4, +(state.zoom + 0.1).toFixed(2)) })} data-testid="ie-zoom-in" className="p-2 border border-[#E5DEC9] bg-white"><ZoomIn size={14} /></button>
              </div>
              <p className="text-[10px] text-[#9a938a] mt-1">Drag the image on the canvas to reposition · {Math.round(state.zoom * 100)}%</p>
            </div>
            <div>
              <p className="tq-overline mb-2">Rotate & Flip</p>
              <div className="grid grid-cols-4 gap-1.5">
                <button onClick={() => commit({ rot: (state.rot - 90 + 360) % 360 })} data-testid="ie-rotate-left" className="p-2 border border-[#E5DEC9] bg-white flex justify-center" aria-label="Rotate left"><RotateCcw size={15} /></button>
                <button onClick={() => commit({ rot: (state.rot + 90) % 360 })} data-testid="ie-rotate-right" className="p-2 border border-[#E5DEC9] bg-white flex justify-center" aria-label="Rotate right"><RotateCw size={15} /></button>
                <button onClick={() => commit({ flipH: !state.flipH })} data-testid="ie-flip-h" className={`p-2 border flex justify-center ${state.flipH ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`} aria-label="Flip horizontal"><FlipHorizontal2 size={15} /></button>
                <button onClick={() => commit({ flipV: !state.flipV })} data-testid="ie-flip-v" className={`p-2 border flex justify-center ${state.flipV ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`} aria-label="Flip vertical"><FlipVertical2 size={15} /></button>
              </div>
            </div>
            <div>
              <p className="tq-overline mb-2">Canvas Background</p>
              <div className="flex gap-1.5 items-center">
                {[["white", "White"], ["transparent", "Transparent"], ["custom", "Custom"]].map(([v, l]) => (
                  <button key={v} onClick={() => commit({ bgType: v })} data-testid={`ie-bg-${v}`}
                    className={`px-2.5 py-1.5 text-[11px] border ${state.bgType === v ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`}>{l}</button>
                ))}
                {state.bgType === "custom" && <input type="color" value={state.bgColor} onChange={(e) => commit({ bgColor: e.target.value })} data-testid="ie-bg-color" className="w-9 h-9 border border-[#E5DEC9] cursor-pointer" />}
              </div>
            </div>
            <div className="pt-3 border-t border-[#E5DEC9] space-y-2">
              <button onClick={() => save("replace")} disabled={!!saving} data-testid="ie-save-replace" className="tq-btn-dark w-full">
                {saving === "replace" ? "Saving…" : "Save Changes (Replace — original kept safe)"}
              </button>
              <button onClick={() => save("new")} disabled={!!saving} data-testid="ie-save-new" className="tq-btn-outline w-full">
                {saving === "new" ? "Saving…" : "Save as New Image"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
